import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import GlassPanel from "@/components/GlassPanel";
import api from "@/lib/api";
import { ArrowLeft, Play } from "lucide-react";

const PILLAR_NAMES = {
  strength: "Strength (S)",
  speed: "Speed (F)",
  explosive: "Explosive (E)",
  stamina: "Stamina (P)",
  control: "Control (C)",
};

export default function TestDetail() {
  const { key } = useParams();
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/tests/${key}`).then((r) => setTest(r.data.test)).finally(() => setLoading(false));
  }, [key]);

  if (loading) {
    return <div className="min-h-[50vh] grid place-items-center text-white gps-mono text-sm tracking-[0.3em]">LOADING TEST...</div>;
  }
  if (!test) {
    return <div className="text-center py-24 text-mute gps-mono">Test not found.</div>;
  }

  const qdrM = test.qdr_M || [0, 0, 0];
  const qdrF = test.qdr_F || [0, 0, 0];

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12" data-testid={`test-detail-${test.key}`}>
      <Link to="/" className="gps-btn ghost mb-8 inline-flex" data-testid="test-back">
        <ArrowLeft size={14} /> BACK
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Video placeholder */}
        <GlassPanel className="aspect-video overflow-hidden relative">
          {test.video_url ? (
            <iframe
              title={test.name}
              src={test.video_url}
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; encrypted-media; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <div className="absolute inset-0 grid place-items-center">
              <div className="text-center">
                <Play size={48} className="mx-auto text-white opacity-60" />
                <div className="gps-mono text-mute text-xs mt-3 tracking-[0.25em]">VIDEO COMING SOON</div>
              </div>
            </div>
          )}
        </GlassPanel>

        {/* Details */}
        <div className="flex flex-col gap-4">
          <GlassPanel className="p-7">
            <div className="gps-tag mb-3">PROTOCOL · {PILLAR_NAMES[test.pillar]?.toUpperCase()}</div>
            <h1 className="gps-heading text-4xl md:text-5xl">{test.name.toUpperCase()}</h1>
            <p className="text-white/85 leading-relaxed mt-5">{test.description}</p>
          </GlassPanel>

          {/* QDR */}
          <GlassPanel className="p-6">
            <div className="gps-label mb-4">QDR CALIBRATION ({test.unit})</div>
            <div className="grid grid-cols-3 gap-3">
              {["Q", "D", "R"].map((label, i) => (
                <div key={label} className="border border-white/15 rounded-lg p-4 text-center">
                  <div className="gps-mono text-mute text-[10px] tracking-[0.25em]">{label}</div>
                  <div className="big-numeric text-white text-2xl mt-1">{qdrM[i]}</div>
                  <div className="gps-mono text-[9px] text-mute mt-2 tracking-[0.18em]">MEN</div>
                  <div className="hud-divider my-2" />
                  <div className="big-numeric text-white text-2xl">{qdrF[i]}</div>
                  <div className="gps-mono text-[9px] text-mute mt-2 tracking-[0.18em]">WOMEN</div>
                </div>
              ))}
            </div>
            <div className="text-mute text-[10px] gps-mono mt-4 tracking-[0.18em]">
              Q · MINIMUM VALID · D · APPROX TOP 10% · R · KNOWN PEAK
            </div>
          </GlassPanel>
        </div>
      </div>
    </div>
  );
}
